import { saveAs } from "file-saver";

const loadPptxGenJS = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("PPT export is only available in the browser"));
  }

  if (window.PptxGenJS) {
    return Promise.resolve(window.PptxGenJS);
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-pptxgenjs]');
    if (existingScript) {
      existingScript.addEventListener("load", () => {
        resolve(window.PptxGenJS || window.pptxgen);
      }, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load PPT export library")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/pptxgenjs@4.0.1/dist/pptxgen.bundle.js";
    script.async = true;
    script.setAttribute("data-pptxgenjs", "true");
    script.onload = () => {
      resolve(window.PptxGenJS || window.pptxgen);
    };
    script.onerror = () => reject(new Error("Failed to load PPT export library"));
    document.head.appendChild(script);
  });
};

const parseAnalysisReportData = (analysisResultId) => {
  const reportData = JSON.parse(analysisResultId?.reportData || "{}");

  return {
    reportName: analysisResultId?.reportName || "Report",
    fleet: [reportData?.fleet || {}],
    fleetMix: [...(reportData?.fleetMix || [])],
    maintenance: [reportData?.maintainanceCost || {}],
    cycling: [reportData?.cyclingComparision || {}],
    fuelCost: [reportData?.fuelCost || {}],
  };
};

const formatCsvCell = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value).replace(/"/g, '""');
  return `"${stringValue}"`;
};

export const generateCsvContent = (analysisResultId) => {
  const { reportName, fleet, maintenance, fuelCost, cycling, fleetMix } = parseAnalysisReportData(analysisResultId);
  const rows = [];

  const pushSection = (sectionName, headers, dataRows) => {
    rows.push([sectionName]);
    rows.push(headers);
    dataRows.forEach((item) => rows.push(headers.map((header) => item?.[header] ?? "")));
    rows.push([]);
  };

  rows.push([`Analysis Report: ${reportName}`]);
  rows.push([]);

  pushSection(
    "Fleet",
    ["Current Fleet Size", "Avg. Vehicle Model Year", "Avg. New Vehicle Price", "Avg. Current Mileage", "Current Cycles", "Avg. Annual Miles"],
    fleet.map((item) => ({
      "Current Fleet Size": item?.currentFleetSize,
      "Avg. Vehicle Model Year": item?.avgVehicleYear,
      "Avg. New Vehicle Price": item?.avgNewVehiclePrice,
      "Avg. Current Mileage": Math.round(item?.currentMileage || 0),
      "Current Cycles": item?.currentCycle,
      "Avg. Annual Miles": Math.round(item?.annualMiles || 0),
    }))
  );

  pushSection(
    "Maintenance Cost",
    ["Total Annualized Spend", "Per Vehicle Spend", "Vehicle Spend/Month", "Avg. Cost Per Mile Per Vehicle Spend"],
    maintenance.map((item) => ({
      "Total Annualized Spend": item?.totalAnnualized,
      "Per Vehicle Spend": item?.vehicleSpend,
      "Vehicle Spend/Month": item?.vehicleSpendMonth,
      "Avg. Cost Per Mile Per Vehicle Spend": item?.costPerMile,
    }))
  );

  pushSection(
    "Fuel Cost",
    ["Annual Spend Vehicle", "Avg. Annual Miles", "Avg. Mpg", "Cost Per Gallon", "Monthly Spend Vehicle", "Number Of Vehicle", "Total Fuel Spend"],
    fuelCost.map((item) => ({
      "Annual Spend Vehicle": item?.annualSpendVehicle,
      "Avg. Annual Miles": Math.round(item?.avgAnnualMiles || 0),
      "Avg. Mpg": item?.avgMpg,
      "Cost Per Gallon": item?.costPerGallon,
      "Monthly Spend Vehicle": item?.monthlySpendVehicle,
      "Number Of Vehicle": item?.numberOfVehicle,
      "Total Fuel Spend": item?.totalFuelSpend,
    }))
  );

  pushSection(
    "Cycling Comparison",
    ["Avg. Year 1 Cost Per Miles", "Avg. Year 1 MPG", "Fuel Spend Year 1", "Monthly Lease Cost", "Monthly Maintanance Year1", "ProposedCycle"],
    cycling.map((item) => ({
      "Avg. Year 1 Cost Per Miles": item?.avgYear1CostPerMiles,
      "Avg. Year 1 MPG": item?.avgYear1Mpg,
      "Fuel Spend Year 1": item?.fuelSpendY1,
      "Monthly Lease Cost": item?.monthlyLeaseCost,
      "Monthly Maintanance Year1": item?.monthlyMaintananceYear1,
      "ProposedCycle": item?.proposedCycle,
    }))
  );

  pushSection(
    "Fleet Mix",
    ["Fiscal Year", "Fleet Size", "Annual Needs", "Purchased/Financed", "Cycling Payment", "Cash Outlay", "Current Payment", "Maintenance", "Fuel", "Fleet Spend", "Vehicle Equity", "Savings"],
    fleetMix.map((item) => ({
      "Fiscal Year": item?.fiscalYear,
      "Fleet Size": item?.fleetSize,
      "Annual Needs": item?.annualNeeds,
      "Purchased/Financed": item?.purchasedFinancial,
      "Cycling Payment": item?.cyclingPayment,
      "Cash Outlay": item?.cashOutlay,
      "Current Payment": item?.currentPayment,
      Maintenance: item?.maintenance,
      Fuel: item?.fuel,
      "Fleet Spend": item?.fleetSpeed,
      "Vehicle Equity": item?.vehicleEquity,
      Savings: item?.savings,
    }))
  );

  return rows.map((row) => row.map(formatCsvCell).join(",")).join("\n");
};

export const handleExportToCSV = (analysisResultId) => {
  const { reportName } = parseAnalysisReportData(analysisResultId);
  const csvContent = generateCsvContent(analysisResultId);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `${reportName}.csv`);
};

export const handleExportToPPT = async (analysisResultId) => {
  const { reportName, fleet, maintenance, fuelCost, cycling, fleetMix } = parseAnalysisReportData(analysisResultId);
  const PptxGenJS = await loadPptxGenJS();
  const pptx = typeof PptxGenJS === "function" ? new PptxGenJS() : new PptxGenJS.default();
  pptx.layout = "LAYOUT_WIDE";

  const titleSlide = pptx.addSlide();
  titleSlide.background = { color: "F8FAFC" };
  titleSlide.addText("Analysis Report", {
    x: 0.6,
    y: 0.6,
    fontSize: 28,
    bold: true,
    color: "0F172A",
  });
  titleSlide.addText(reportName, {
    x: 0.6,
    y: 1.55,
    fontSize: 18,
    color: "334155",
  });

  const coreMetrics = [
    `Fleet Size: ${fleet?.[0]?.currentFleetSize ?? "-"}`,
    `Annual Miles: ${fleet?.[0]?.annualMiles ?? "-"}`,
    `Total Annualized Spend: ${maintenance?.[0]?.totalAnnualized ?? "-"}`,
    `Total Fuel Spend: ${fuelCost?.[0]?.totalFuelSpend ?? "-"}`,
    `Total Savings: ${fleetMix.reduce((sum, item) => sum + (item?.savings || 0), 0)}`,
  ];

  titleSlide.addText(coreMetrics.join("\n"), {
    x: 0.7,
    y: 2.3,
    fontSize: 14,
    color: "475569",
    breakLine: true,
    margin: 0.08,
  });

  const summarySlide = pptx.addSlide();
  summarySlide.addText("Report Summary", {
    x: 0.6,
    y: 0.4,
    fontSize: 24,
    bold: true,
    color: "0F172A",
  });

  summarySlide.addTable(
    [
      [
        { text: "Section", options: { bold: true, fill: { color: "E2E8F0" } } },
        { text: "Metric", options: { bold: true, fill: { color: "E2E8F0" } } },
        { text: "Value", options: { bold: true, fill: { color: "E2E8F0" } } },
      ],
      ["Fleet", "Current Fleet Size", fleet?.[0]?.currentFleetSize ?? "-"],
      ["Maintenance", "Total Annualized Spend", maintenance?.[0]?.totalAnnualized ?? "-"],
      ["Fuel Cost", "Total Fuel Spend", fuelCost?.[0]?.totalFuelSpend ?? "-"],
      ["Cycling", "Proposed Cycle", cycling?.[0]?.proposedCycle ?? "-"],
      ["Fleet Mix", "Total Savings", fleetMix.reduce((sum, item) => sum + (item?.savings || 0), 0)],
    ],
    {
      x: 0.45,
      y: 1.1,
      w: 12.2,
      h: 4.8,
      border: { color: "CBD5E1", pt: 1, style: "solid" },
    }
  );

  const createTableSlide = (title, headers, dataRows) => {
    const slide = pptx.addSlide();
    slide.addText(title, {
      x: 0.6,
      y: 0.4,
      fontSize: 24,
      bold: true,
      color: "0F172A",
    });

    const fontSize = headers.length > 8 ? 9 : 12;

    const tableData = [
      headers.map((h) => ({ text: h, options: { bold: true, fill: { color: "E2E8F0" }, fontSize } })),
      ...dataRows.map((row) => headers.map((h) => ({ text: String(row[h] ?? "-"), options: { fontSize } }))),
    ];

    slide.addTable(tableData, {
      x: 0.45,
      y: 1.1,
      w: 12.2,
      border: { color: "CBD5E1", pt: 1, style: "solid" },
      autoPage: true,
    });
  };

  createTableSlide(
    "Fleet Details",
    ["Current Fleet Size", "Avg. Vehicle Model Year", "Avg. New Vehicle Price", "Avg. Current Mileage", "Current Cycles", "Avg. Annual Miles"],
    fleet.map((item) => ({
      "Current Fleet Size": item?.currentFleetSize,
      "Avg. Vehicle Model Year": item?.avgVehicleYear,
      "Avg. New Vehicle Price": item?.avgNewVehiclePrice,
      "Avg. Current Mileage": Math.round(item?.currentMileage || 0),
      "Current Cycles": item?.currentCycle,
      "Avg. Annual Miles": Math.round(item?.annualMiles || 0),
    }))
  );

  createTableSlide(
    "Maintenance Cost",
    ["Total Annualized Spend", "Per Vehicle Spend", "Vehicle Spend/Month", "Avg. Cost Per Mile Per Vehicle Spend"],
    maintenance.map((item) => ({
      "Total Annualized Spend": item?.totalAnnualized,
      "Per Vehicle Spend": item?.vehicleSpend,
      "Vehicle Spend/Month": item?.vehicleSpendMonth,
      "Avg. Cost Per Mile Per Vehicle Spend": item?.costPerMile,
    }))
  );

  createTableSlide(
    "Fuel Cost",
    ["Annual Spend Vehicle", "Avg. Annual Miles", "Avg. Mpg", "Cost Per Gallon", "Monthly Spend Vehicle", "Number Of Vehicle", "Total Fuel Spend"],
    fuelCost.map((item) => ({
      "Annual Spend Vehicle": item?.annualSpendVehicle,
      "Avg. Annual Miles": Math.round(item?.avgAnnualMiles || 0),
      "Avg. Mpg": item?.avgMpg,
      "Cost Per Gallon": item?.costPerGallon,
      "Monthly Spend Vehicle": item?.monthlySpendVehicle,
      "Number Of Vehicle": item?.numberOfVehicle,
      "Total Fuel Spend": item?.totalFuelSpend,
    }))
  );

  createTableSlide(
    "Cycling Comparison",
    ["Avg. Year 1 Cost Per Miles", "Avg. Year 1 MPG", "Fuel Spend Year 1", "Monthly Lease Cost", "Monthly Maintanance Year1", "ProposedCycle"],
    cycling.map((item) => ({
      "Avg. Year 1 Cost Per Miles": item?.avgYear1CostPerMiles,
      "Avg. Year 1 MPG": item?.avgYear1Mpg,
      "Fuel Spend Year 1": item?.fuelSpendY1,
      "Monthly Lease Cost": item?.monthlyLeaseCost,
      "Monthly Maintanance Year1": item?.monthlyMaintananceYear1,
      "ProposedCycle": item?.proposedCycle,
    }))
  );

  createTableSlide(
    "Fleet Mix",
    ["Fiscal Year", "Fleet Size", "Annual Needs", "Purchased/Financed", "Cycling Payment", "Cash Outlay", "Current Payment", "Maintenance", "Fuel", "Fleet Spend", "Vehicle Equity", "Savings"],
    fleetMix.map((item) => ({
      "Fiscal Year": item?.fiscalYear,
      "Fleet Size": item?.fleetSize,
      "Annual Needs": item?.annualNeeds,
      "Purchased/Financed": item?.purchasedFinancial,
      "Cycling Payment": item?.cyclingPayment,
      "Cash Outlay": item?.cashOutlay,
      "Current Payment": item?.currentPayment,
      "Maintenance": item?.maintenance,
      "Fuel": item?.fuel,
      "Fleet Spend": item?.fleetSpeed,
      "Vehicle Equity": item?.vehicleEquity,
      "Savings": item?.savings,
    }))
  );

  await pptx.writeFile({ fileName: `${reportName}.pptx` });
};
